import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, Trash2, Edit, Eye, Gift, Clock, MapPin, 
  DollarSign, Star, MoreVertical, Search, Calendar, BarChart3, Heart
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import ImageUpload from '@/components/ImageUpload';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { PartnerLayout } from '@/components/PartnerLayout';
import api from '@/lib/api';
import { toast } from 'sonner';

const ExperienceManagement = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isPartner, user, loading: authLoading, refreshUser } = useAuth();
  const [experiences, setExperiences] = useState([]);
  const [venues, setVenues] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingExperience, setEditingExperience] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    venue_id: '',
    venue_name: '',
    price: '',
    gold_price: '',
    valid_until: '',
    terms: '',
    is_date_night: false,
    quantity_limit: '',
    low_inventory_threshold: '10',
    blackout_dates: [],
    images: []
  });

  useEffect(() => {
    const init = async () => {
      if (authLoading) return;
      
      if (!isAuthenticated) {
        navigate('/auth');
        return;
      }
      
      if (!user) {
        try {
          await refreshUser();
        } catch (error) {
          navigate('/auth');
          return;
        }
      }
      
      if (!isPartner) {
        navigate('/dashboard');
        return;
      }
      
      fetchData();
    };
    
    init();
  }, [isAuthenticated, isPartner, authLoading, user]);

  const fetchData = async () => {
    try {
      const [experiencesRes, venuesRes] = await Promise.all([
        api.get('/partner/experiences'),
        api.get('/partner/venues')  // Only fetch partner's own venues
      ]);
      setExperiences(experiencesRes.data);
      setVenues(venuesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required venue
    if (!formData.venue_id) {
      toast.error('Please select a venue for this experience');
      return;
    }
    
    // Validate required image
    if (formData.images.length === 0) {
      toast.error('Please add at least one photo for this experience');
      return;
    }
    
    setLoading(true);
    try {
      const selectedVenue = venues.find(v => v.id === formData.venue_id);
      const payload = {
        ...formData,
        venue_name: selectedVenue?.name || formData.venue_name,
        price: parseFloat(formData.price),
        gold_price: formData.gold_price ? parseFloat(formData.gold_price) : null,
        quantity_limit: formData.quantity_limit ? parseInt(formData.quantity_limit) : null,
        low_inventory_threshold: formData.low_inventory_threshold ? parseInt(formData.low_inventory_threshold) : 10,
        neighborhood: selectedVenue?.neighborhood || selectedVenue?.district || null
      };
      
      if (editingExperience) {
        await api.put(`/vouchers/${editingExperience.id}`, payload);
        toast.success('Experience updated successfully!');
      } else {
        await api.post('/vouchers', payload);
        toast.success('Experience created successfully!');
      }
      
      setShowForm(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Experience save error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save experience');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      venue_id: '',
      venue_name: '',
      price: '',
      gold_price: '',
      valid_until: '',
      terms: '',
      is_date_night: false,
      quantity_limit: '',
      low_inventory_threshold: '10',
      blackout_dates: [],
      images: []
    });
    setEditingExperience(null);
  };

  const handleEdit = (experience) => {
    setEditingExperience(experience);
    setFormData({
      title: experience.title || '',
      description: experience.description || '',
      venue_id: experience.venue_id || '',
      venue_name: experience.venue_name || '',
      price: experience.price?.toString() || '',
      gold_price: experience.gold_price?.toString() || '',
      valid_until: experience.valid_until || '',
      terms: experience.terms || '',
      is_date_night: experience.is_date_night || false,
      quantity_limit: experience.quantity_limit?.toString() || '',
      low_inventory_threshold: experience.low_inventory_threshold?.toString() || '10',
      blackout_dates: experience.blackout_dates || [],
      images: experience.images || []
    });
    setShowForm(true);
  };

  const handleDelete = async (experienceId) => {
    if (!window.confirm('Are you sure you want to delete this experience?')) return;
    
    try {
      await api.delete(`/vouchers/${experienceId}`);
      toast.success('Experience deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete experience');
    }
  };

  const filteredExperiences = experiences.filter(exp =>
    exp.title?.toLowerCase().includes(search.toLowerCase()) ||
    exp.venue_name?.toLowerCase().includes(search.toLowerCase())
  );

  if (!isAuthenticated || !isPartner) return null;

  return (
    <PartnerLayout 
      title="Experiences"
      subtitle="Create and manage vouchers & experiences"
      actions={
        <Dialog open={showForm} onOpenChange={(open) => {
          setShowForm(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-purple-600 hover:bg-purple-700">
              <Plus className="w-4 h-4 mr-2" />
              Create Experience
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingExperience ? 'Edit Experience' : 'Create New Experience'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Experience Title *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Dinner for Two"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="venue">Venue *</Label>
                  <Select 
                    value={formData.venue_id} 
                    onValueChange={(v) => {
                      const venue = venues.find(ven => ven.id === v);
                      setFormData({ 
                        ...formData, 
                        venue_id: v,
                        venue_name: venue?.name || ''
                      });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select venue" />
                    </SelectTrigger>
                    <SelectContent>
                      {venues.map((venue) => (
                        <SelectItem key={venue.id} value={venue.id}>
                          {venue.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Image Upload Section */}
              <ImageUpload
                images={formData.images}
                onImagesChange={(newImages) => setFormData({ ...formData, images: newImages })}
                maxImages={5}
                label="Experience Photos * (at least 1 required)"
              />

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="price">Price ($) *</Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="99.00"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gold_price">Gold Price ($)</Label>
                  <Input
                    id="gold_price"
                    type="number"
                    step="0.01"
                    value={formData.gold_price}
                    onChange={(e) => setFormData({ ...formData, gold_price: e.target.value })}
                    placeholder="79.00"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="quantity_limit">Quantity Available</Label>
                  <Input
                    id="quantity_limit"
                    type="number"
                    min="1"
                    value={formData.quantity_limit}
                    onChange={(e) => setFormData({ ...formData, quantity_limit: e.target.value })}
                    placeholder="Unlimited"
                    data-testid="quantity-limit-input"
                  />
                  <p className="text-xs text-muted-foreground">Leave empty for unlimited</p>
                </div>
              </div>

              {formData.quantity_limit && (
                <div className="p-4 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <div className="space-y-2">
                    <Label htmlFor="low_inventory_threshold" className="text-amber-800 dark:text-amber-400">
                      Low Inventory Alert Threshold
                    </Label>
                    <div className="flex items-center gap-3">
                      <Input
                        id="low_inventory_threshold"
                        type="number"
                        min="1"
                        value={formData.low_inventory_threshold}
                        onChange={(e) => setFormData({ ...formData, low_inventory_threshold: e.target.value })}
                        className="w-24"
                        data-testid="low-inventory-threshold-input"
                      />
                      <span className="text-sm text-muted-foreground">remaining</span>
                    </div>
                    <p className="text-xs text-amber-700 dark:text-amber-400">
                      You'll receive an email alert when inventory drops to this number or below
                    </p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="valid_until">Valid Until *</Label>
                  <Input
                    id="valid_until"
                    type="date"
                    value={formData.valid_until}
                    onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Blackout Dates</Label>
                  
                  {/* Preset Buttons */}
                  <div className="flex flex-wrap gap-2 mb-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="text-xs"
                      onClick={() => {
                        // Block all weekends for the next 3 months
                        const weekends = [];
                        const today = new Date();
                        for (let i = 0; i < 90; i++) {
                          const date = new Date(today);
                          date.setDate(today.getDate() + i);
                          const day = date.getDay();
                          if (day === 0 || day === 6) { // Sunday = 0, Saturday = 6
                            weekends.push(date.toISOString().split('T')[0]);
                          }
                        }
                        const newDates = [...new Set([...formData.blackout_dates, ...weekends])].sort();
                        setFormData({ ...formData, blackout_dates: newDates });
                        toast.success('Blocked all weekends for the next 3 months');
                      }}
                    >
                      + Block Weekends
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="text-xs"
                      onClick={() => {
                        // Block all Mondays for the next 3 months
                        const mondays = [];
                        const today = new Date();
                        for (let i = 0; i < 90; i++) {
                          const date = new Date(today);
                          date.setDate(today.getDate() + i);
                          if (date.getDay() === 1) {
                            mondays.push(date.toISOString().split('T')[0]);
                          }
                        }
                        const newDates = [...new Set([...formData.blackout_dates, ...mondays])].sort();
                        setFormData({ ...formData, blackout_dates: newDates });
                        toast.success('Blocked all Mondays for the next 3 months');
                      }}
                    >
                      + Block Mondays
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="text-xs"
                      onClick={() => {
                        // Block specific day of week (Tuesdays)
                        const tuesdays = [];
                        const today = new Date();
                        for (let i = 0; i < 90; i++) {
                          const date = new Date(today);
                          date.setDate(today.getDate() + i);
                          if (date.getDay() === 2) {
                            tuesdays.push(date.toISOString().split('T')[0]);
                          }
                        }
                        const newDates = [...new Set([...formData.blackout_dates, ...tuesdays])].sort();
                        setFormData({ ...formData, blackout_dates: newDates });
                        toast.success('Blocked all Tuesdays for the next 3 months');
                      }}
                    >
                      + Block Tuesdays
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="text-xs"
                      onClick={() => {
                        // Block major holidays
                        const year = new Date().getFullYear();
                        const holidays = [
                          `${year}-01-01`, // New Year's Day
                          `${year}-02-14`, // Valentine's Day
                          `${year}-07-04`, // Independence Day
                          `${year}-10-31`, // Halloween
                          `${year}-11-28`, // Thanksgiving (approximate)
                          `${year}-12-24`, // Christmas Eve
                          `${year}-12-25`, // Christmas
                          `${year}-12-31`, // New Year's Eve
                          `${year + 1}-01-01`, // Next New Year's Day
                          `${year + 1}-02-14`, // Next Valentine's Day
                        ];
                        const newDates = [...new Set([...formData.blackout_dates, ...holidays])].sort();
                        setFormData({ ...formData, blackout_dates: newDates });
                        toast.success('Blocked major holidays');
                      }}
                    >
                      + Block Holidays
                    </Button>
                    {formData.blackout_dates.length > 0 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-xs text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => {
                          setFormData({ ...formData, blackout_dates: [] });
                          toast.success('Cleared all blackout dates');
                        }}
                      >
                        Clear All
                      </Button>
                    )}
                  </div>
                  
                  {/* Manual Date Input */}
                  <div className="flex gap-2">
                    <Input
                      type="date"
                      id="blackout_date_input"
                      className="flex-1"
                      min={new Date().toISOString().split('T')[0]}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const input = document.getElementById('blackout_date_input');
                        if (input.value && !formData.blackout_dates.includes(input.value)) {
                          setFormData({ ...formData, blackout_dates: [...formData.blackout_dates, input.value].sort() });
                          input.value = '';
                        }
                      }}
                    >
                      Add Date
                    </Button>
                  </div>
                  
                  {/* Display Selected Dates */}
                  {formData.blackout_dates.length > 0 && (
                    <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-muted-foreground">
                          {formData.blackout_dates.length} date{formData.blackout_dates.length !== 1 ? 's' : ''} blocked
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto">
                        {formData.blackout_dates.map((date, idx) => (
                          <Badge key={idx} variant="secondary" className="text-xs">
                            {new Date(date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            <button
                              type="button"
                              onClick={() => setFormData({ ...formData, blackout_dates: formData.blackout_dates.filter((_, i) => i !== idx) })}
                              className="ml-1 text-red-500 hover:text-red-700"
                            >
                              ×
                            </button>
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">Dates when this experience cannot be redeemed</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  rows={4}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe what's included in this experience..."
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="terms">Terms & Conditions</Label>
                <Textarea
                  id="terms"
                  rows={3}
                  value={formData.terms}
                  onChange={(e) => setFormData({ ...formData, terms: e.target.value })}
                  placeholder="Valid for dine-in only. Cannot be combined with other offers..."
                />
              </div>

              {/* Date Night Hub Checkbox */}
              <div className="flex items-start gap-3 p-4 bg-rose-50 dark:bg-rose-950/20 rounded-lg border border-rose-200 dark:border-rose-800">
                <Checkbox
                  id="is_date_night"
                  checked={formData.is_date_night}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_date_night: checked })}
                  className="mt-0.5"
                  data-testid="date-night-checkbox"
                />
                <div className="flex-1">
                  <Label htmlFor="is_date_night" className="text-sm font-semibold cursor-pointer flex items-center gap-2">
                    <Heart className="w-4 h-4 text-rose-500 fill-rose-500" />
                    Feature in Date Night Hub
                  </Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    This experience will appear in the Date Night Hub and be recommended for couples looking for romantic experiences.
                  </p>
                </div>
              </div>

              {formData.gold_price && (
                <div className="flex items-center gap-2 p-3 bg-yellow-50 dark:bg-yellow-950/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                  <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                  <span className="text-sm">
                    Gold members will save <strong>${(parseFloat(formData.price || 0) - parseFloat(formData.gold_price || 0)).toFixed(2)}</strong> on this experience
                  </span>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setShowForm(false);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : editingExperience ? 'Update Experience' : 'Create Experience'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      }
    >
      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search experiences..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-purple-100 dark:bg-purple-950 flex items-center justify-center">
              <Gift className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{experiences.length}</p>
              <p className="text-sm text-muted-foreground">Total Experiences</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-green-100 dark:bg-green-950 flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">
                ${experiences.reduce((sum, e) => sum + (e.price || 0), 0).toFixed(0)}
              </p>
              <p className="text-sm text-muted-foreground">Total Value</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-yellow-100 dark:bg-yellow-950 flex items-center justify-center">
              <Star className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">
                {experiences.filter(e => e.gold_price).length}
              </p>
              <p className="text-sm text-muted-foreground">Gold Deals</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Experiences Grid */}
      {filteredExperiences.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Gift className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-bold text-foreground mb-2">No experiences yet</h3>
            <p className="text-muted-foreground mb-4">
              Create vouchers and experiences to offer exclusive deals to customers
            </p>
            <Button onClick={() => setShowForm(true)} className="bg-purple-600 hover:bg-purple-700">
              <Plus className="w-4 h-4 mr-2" />
              Create Experience
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredExperiences.map((experience) => (
            <Card 
              key={experience.id} 
              className="overflow-hidden hover:shadow-lg transition-shadow group cursor-pointer"
              onClick={() => navigate(`/partner/experiences/${experience.id}`)}
            >
              <div className="aspect-video bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-950 dark:to-pink-950 relative">
                {experience.images?.[0] ? (
                  <img src={experience.images[0]} alt={experience.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Gift className="w-12 h-12 text-purple-400" />
                  </div>
                )}
                {experience.is_date_night && (
                  <Badge className="absolute top-3 left-3 bg-rose-500 text-white border-0">
                    <Heart className="w-3 h-3 mr-1 fill-current" />
                    Date Night
                  </Badge>
                )}
                <div className="absolute top-3 right-3">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="secondary" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/partner/experiences/${experience.id}`); }}>
                        <BarChart3 className="w-4 h-4 mr-2" />
                        Manage Experience
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/partner/experiences/${experience.id}/builder`); }}>
                        <Edit className="w-4 h-4 mr-2" />
                        Edit Page
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/experiences/${experience.id}`); }}>
                        <Eye className="w-4 h-4 mr-2" />
                        View Public Page
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDelete(experience.id); }} className="text-red-600">
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div className="absolute top-3 left-3 flex gap-2">
                  <Badge className="bg-purple-600 text-white border-0">
                    VOUCHER
                  </Badge>
                  {experience.gold_price && (
                    <Badge className="bg-yellow-400 text-yellow-900 border-0">
                      <Star className="w-3 h-3 mr-1 fill-current" />
                      GOLD
                    </Badge>
                  )}
                </div>
              </div>
              <CardContent className="p-4">
                <h3 className="font-bold text-foreground mb-2 line-clamp-1">{experience.title}</h3>
                <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{experience.description}</p>
                <div className="space-y-1 text-sm text-muted-foreground mb-3">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    {experience.venue_name}
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Valid until {experience.valid_until}
                  </div>
                </div>
                <div className="flex items-center justify-between pt-3 border-t">
                  <div>
                    <span className="text-xl font-bold text-foreground">${experience.price}</span>
                    {experience.gold_price && (
                      <span className="text-sm text-yellow-600 ml-2">
                        ${experience.gold_price} Gold
                      </span>
                    )}
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); handleEdit(experience); }}
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </PartnerLayout>
  );
};

export default ExperienceManagement;
